import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = t("newsletter.validation.nameRequired");
    if (!EMAIL_RE.test(values.email.trim())) next.email = t("newsletter.validation.emailRequired");
    if (!values.consent) next.consent = t("newsletter.validation.consentRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // No backend needed - route to Thank You page (Spanish only, "/thank_you")
    setTimeout(() => navigate("/thank_you"), 250);
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="grid gap-4"
      data-testid="newsletter-form"
    >
      <div>
        <label htmlFor="nl-name" className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0b3d5c]">
          {t("newsletter.nameLabel")}
        </label>
        <input
          id="nl-name"
          type="text"
          value={values.name}
          onChange={(e) => setField("name", e.target.value)}
          placeholder={t("newsletter.namePlaceholder")}
          className="mt-1 w-full rounded-md border border-[#c9d6e0] bg-white px-4 py-3 text-sm text-[#0f1f2c] placeholder:text-[#8598a8] focus:border-[#0f5c3f] focus:outline-none focus:ring-2 focus:ring-[#0f5c3f]/25"
          data-testid="newsletter-name-input"
          autoComplete="name"
        />
        {errors.name && (
          <p className="mt-1 text-xs text-[#a52a2a]" data-testid="newsletter-name-error">
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="nl-email" className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0b3d5c]">
          {t("newsletter.emailLabel")}
        </label>
        <input
          id="nl-email"
          type="email"
          value={values.email}
          onChange={(e) => setField("email", e.target.value)}
          placeholder={t("newsletter.emailPlaceholder")}
          className="mt-1 w-full rounded-md border border-[#c9d6e0] bg-white px-4 py-3 text-sm text-[#0f1f2c] placeholder:text-[#8598a8] focus:border-[#0f5c3f] focus:outline-none focus:ring-2 focus:ring-[#0f5c3f]/25"
          data-testid="newsletter-email-input"
          autoComplete="email"
        />
        {errors.email && (
          <p className="mt-1 text-xs text-[#a52a2a]" data-testid="newsletter-email-error">
            {errors.email}
          </p>
        )}
      </div>

      <label className="flex items-start gap-3 text-xs leading-relaxed text-[#4b5f70]">
        <input
          type="checkbox"
          checked={values.consent}
          onChange={(e) => setField("consent", e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-[#c9d6e0] text-[#0f5c3f] focus:ring-[#0f5c3f]"
          data-testid="newsletter-consent-checkbox"
        />
        <span>{t("newsletter.consentLabel")}</span>
      </label>
      {errors.consent && (
        <p className="-mt-2 text-xs text-[#a52a2a]" data-testid="newsletter-consent-error">
          {errors.consent}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-2 rounded-full bg-[#0f5c3f] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0d4d35] disabled:opacity-60"
        data-testid="newsletter-submit-button"
      >
        {submitting ? t("cta.sending") : t("newsletter.submit")}
      </button>
      <p className="text-xs text-[#6e8394]">{t("newsletter.privacyNote")}</p>
    </form>
  );
}
